import { computed, ref } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { OrganizerDao } from '@/dao';
import {
    PointType,
    Organizer,
    Federation,
    ParticipantIDType,
    HeatSortPlan,
    Country,
    Club,
    Region,
    type CommunicationType,
    StationType,
    type CupType,
} from '@/objects/generated';
import { BaseObjDao } from '@/dao/BaseObjDao';
import { type SelectLine, type SelectLineValueType } from '@/objects/SelectLine';
import { useLocaleStore } from './localeStore';
import { nameofEnum } from '@/utilities/utils';
import { type RoundType } from '@/objects/generated/enums/RoundType';
import { type PrizeShareType } from '@/objects/generated/enums/PrizeShareType';
import { type SortDirection } from '@/objects/generated/enums/SortDirection';
import { type Gender } from '@/objects/generated/enums/Gender';
import { useEventCommonStoreRef } from './eventCommonStore';

import { LoaderWithReset } from '@/utilities/LoaderWithReset';
import { $tm, createTranslationKey } from '@/locize';
export type CommonValues =
    | 'Genders'
    | 'PrizeShareTypes'
    | 'DecimalPlacesInResults'
    | 'DecimalPlacesInResultsWithUseEvent'
    | 'RankingResolutions'
    | 'RankingResolutionsWithUseEvent'
    | 'RoundTypes'
    | 'RoundTypesWithUseEvent'
    | 'SortDirection'
    | 'PointTypes'
    | 'Organizers'
    | 'Locales'
    | 'Federations'
    | 'IDTypes'
    | 'Countries'
    | 'Clubs'
    | 'ContestantStatus'
    | 'Teams'
    | 'HeatSortPlans'
    | 'CommunicationType'
    | 'Regions'
    | 'CommunicationType'
    | 'StationType';

export const useCommonStore = defineStore('common', () => {
    // gender block
    const Genders = ref<Map<SelectLineValueType, SelectLine>>(
        new Map([
            [null, { text: '---', value: null }],
            [nameofEnum<Gender>('m'), { text: 'genders.male', value: nameofEnum<Gender>('m') }],
            [nameofEnum<Gender>('f'), { text: 'genders.female', value: nameofEnum<Gender>('f') }],
            [nameofEnum<Gender>('n'), { text: 'genders.both', value: nameofEnum<Gender>('n') }],
        ]),
    );

    // TimeSelect block
    const TimeSelect = ref<Map<SelectLineValueType, SelectLine>>(
        new Map([
            ['1', { text: 'general.first', value: '1' }],
            ['2', { text: 'general.last', value: '2' }],
        ]),
    );

    // DecimalPlacesInResults block
    const DecimalPlacesInResults = ref<Map<SelectLineValueType, SelectLine>>(
        new Map([
            [0, { text: 'resultresolution.second', value: 0 }],
            [1, { text: 'resultresolution.tenth', value: 1 }],
            [2, { text: 'resultresolution.hundreth', value: 2 }],
            [3, { text: 'resultresolution.thousandths', value: 3 }],
        ]),
    );

    // DecimalPlacesInResults block
    const RankingResolutions = ref<Map<SelectLineValueType, SelectLine>>(
        new Map([
            [1000, { text: 'rankresolution.second', value: 1000 }],
            [100, { text: 'rankresolution.tenth', value: 100 }],
            [10, { text: 'rankresolution.hundreth', value: 10 }],
            [1, { text: 'rankresolution.thousandths', value: 1 }],
        ]),
    );

    // SortDirection block
    const SortDirections = ref<Map<SelectLineValueType, SelectLine>>(
        new Map([
            [nameofEnum<SortDirection>('Ascending'), { text: 'general.ascending', value: nameofEnum<SortDirection>('Ascending') }],
            [nameofEnum<SortDirection>('Descending'), { text: 'general.desceding', value: nameofEnum<SortDirection>('Descending') }],
        ]),
    );

    // PrizeShareTypes block
    const PrizeShareTypes = ref<Map<SelectLineValueType, SelectLine>>(
        new Map([
            [null, { text: '---', value: null }],
            [
                nameofEnum<PrizeShareType>('PercentageOfStarting'),
                { text: 'pricetype.percentofstarting', value: nameofEnum<PrizeShareType>('PercentageOfStarting') },
            ],
            [nameofEnum<PrizeShareType>('None'), { text: 'pricetype.none', value: nameofEnum<PrizeShareType>('None') }],
            [
                nameofEnum<PrizeShareType>('PercentageOfAll'),
                { text: 'pricetype.percentofregistered', value: nameofEnum<PrizeShareType>('PercentageOfAll') },
            ],
            [nameofEnum<PrizeShareType>('Fixed'), { text: 'pricetype.numbergiven', value: nameofEnum<PrizeShareType>('Fixed') }],
        ]),
    );

    // RoundTypes block
    const RoundTypes = ref<Map<SelectLineValueType, SelectLine>>(
        new Map([
            [nameofEnum<RoundType>('Down'), { text: 'roundtype.down', value: nameofEnum<RoundType>('Down') }],
            [nameofEnum<RoundType>('Round'), { text: 'roundtype.round', value: nameofEnum<RoundType>('Round') }],
            [nameofEnum<RoundType>('Up'), { text: 'roundtype.up', value: nameofEnum<RoundType>('Up') }],
        ]),
    );

    // Contestant status block
    const ContestantStatus = ref<Map<SelectLineValueType, SelectLine>>(
        new Map([
            [1, { text: 'contestantstatus.registered', value: 1 }],
            [9, { text: 'contestantstatus.unregistered', value: 9 }],
            [20, { text: 'contestantstatus.multirace', value: 20 }],
            [100, { text: 'contestantstatus.licenseapproved', value: 100 }],
            [101, { text: 'contestantstatus.licensedenied', value: 101 }],
            [102, { text: 'contestantstatus.licensesuspended', value: 102 }],
            [51, { text: 'contestantstatus.refundeddonated', value: 51 }],
            [52, { text: 'contestantstatus.refundedrefundapplied', value: 52 }],
            [53, { text: 'contestantstatus.refundedtransferapplied', value: 53 }],
            [54, { text: 'contestantstatus.refundedrefunded', value: 54 }],
            [55, { text: 'contestantstatus.refundedtransferred', value: 55 }],
            [56, { text: 'contestantstatus.refundeddonatedmanual', value: 56 }],
            [8, { text: 'contestantstatus.unknowncontestant', value: 8 }],
            [0, { text: 'contestantstatus.notregistered', value: 0 }],
            [10, { text: 'contestantstatus.soldplace', value: 10 }],
            [11, { text: 'contestantstatus.transferredplace', value: 11 }],
            [20, { text: 'contestantstatus.carouselsignup', value: 20 }],
            [2, { text: 'contestantstatus.licenseregistered', value: 2 }],
        ]),
    );

    const CommunicationTypeAsSelectLine = ref<Map<SelectLineValueType, SelectLine>>(
        new Map([
            [nameofEnum<CommunicationType>('SMS'), { text: 'communicationtype.sms', value: nameofEnum<CommunicationType>('SMS') }],
            [nameofEnum<CommunicationType>('Email'), { text: 'communicationtype.email', value: nameofEnum<CommunicationType>('Email') }],
            [
                nameofEnum<CommunicationType>('SignupConfirmation'),
                { text: 'communicationtype.signupconfirmation', value: nameofEnum<CommunicationType>('SignupConfirmation') },
            ],
            [
                nameofEnum<CommunicationType>('Invoice'),
                { text: 'communicationtype.invoice', value: nameofEnum<CommunicationType>('Invoice') },
            ],
            [
                nameofEnum<CommunicationType>('StartCardEmail'),
                { text: 'communicationtype.startcardemail', value: nameofEnum<CommunicationType>('StartCardEmail') },
            ],
            [
                nameofEnum<CommunicationType>('StartCardSMS'),
                { text: 'communicationtype.startcardsms', value: nameofEnum<CommunicationType>('StartCardSMS') },
            ],
            [
                nameofEnum<CommunicationType>('Giftcard'),
                { text: 'communicationtype.giftcard', value: nameofEnum<CommunicationType>('Giftcard') },
            ],
            [
                nameofEnum<CommunicationType>('CancelRefund'),
                { text: 'communicationtype.cancelrefund', value: nameofEnum<CommunicationType>('CancelRefund') },
            ],
            [
                nameofEnum<CommunicationType>('Transfer'),
                { text: 'communicationtype.transfer', value: nameofEnum<CommunicationType>('Transfer') },
            ],
            [
                nameofEnum<CommunicationType>('Transfer2'),
                { text: 'communicationtype.transfer2', value: nameofEnum<CommunicationType>('Transfer2') },
            ],
        ]),
    );

    const CupTypeAsSelectLine = ref<Map<SelectLineValueType, SelectLine>>(
        new Map([
            [null, { text: 'cuptype.not_set', value: null }],
            [nameofEnum<CupType>('None'), { text: 'cuptype.none', value: nameofEnum<CupType>('None') }],
            [
                nameofEnum<CupType>('NSF_PROLOG_TO_FINALS'),
                { text: 'cuptype.prolog_and_finals', value: nameofEnum<CupType>('NSF_PROLOG_TO_FINALS') },
            ],
            [
                nameofEnum<CupType>('NSF_30_WITH_TIMING_LL_FROM_LEVELS'),
                { text: 'cuptype.nsf_30_with_timing_ll_from_levels', value: nameofEnum<CupType>('NSF_30_WITH_TIMING_LL_FROM_LEVELS') },
            ],
            [
                nameofEnum<CupType>('NSF_30_WITH_TIMING_LL_FROM_PROLOG'),
                { text: 'cuptype.nsf_30_with_timing_ll_from_prolog', value: nameofEnum<CupType>('NSF_30_WITH_TIMING_LL_FROM_PROLOG') },
            ],
            [nameofEnum<CupType>('NSF_6'), { text: 'cuptype.nsf_6', value: nameofEnum<CupType>('NSF_6') }],
            [nameofEnum<CupType>('NSF_12'), { text: 'cuptype.nsf_12', value: nameofEnum<CupType>('NSF_12') }],
            [nameofEnum<CupType>('NSF_16'), { text: 'cuptype.nsf_16', value: nameofEnum<CupType>('NSF_16') }],
            [nameofEnum<CupType>('NSF_18'), { text: 'cuptype.nsf_18', value: nameofEnum<CupType>('NSF_18') }],
            [nameofEnum<CupType>('NSF_24'), { text: 'cuptype.nsf_24', value: nameofEnum<CupType>('NSF_24') }],
            [nameofEnum<CupType>('NSF_50'), { text: 'cuptype.nsf_50', value: nameofEnum<CupType>('NSF_50') }],
            [
                nameofEnum<CupType>('NSF_UP_AND_DOWN_1X4'),
                { text: 'cuptype.nsf_up_and_down_1x4', value: nameofEnum<CupType>('NSF_UP_AND_DOWN_1X4') },
            ],
            [
                nameofEnum<CupType>('NSF_UP_AND_DOWN_1X6'),
                { text: 'cuptype.nsf_up_and_down_1x6', value: nameofEnum<CupType>('NSF_UP_AND_DOWN_1X6') },
            ],
            [
                nameofEnum<CupType>('NSF_UP_AND_DOWN_2X4'),
                { text: 'cuptype.nsf_up_and_down_2x4', value: nameofEnum<CupType>('NSF_UP_AND_DOWN_2X4') },
            ],
            [
                nameofEnum<CupType>('NSF_UP_AND_DOWN_2X6'),
                { text: 'cuptype.nsf_up_and_down_2x6', value: nameofEnum<CupType>('NSF_UP_AND_DOWN_2X6') },
            ],
            [
                nameofEnum<CupType>('NSF_UP_AND_DOWN_3X4'),
                { text: 'cuptype.nsf_up_and_down_3x4', value: nameofEnum<CupType>('NSF_UP_AND_DOWN_3X4') },
            ],
            [
                nameofEnum<CupType>('NSF_UP_AND_DOWN_3X6'),
                { text: 'cuptype.nsf_up_and_down_3x6', value: nameofEnum<CupType>('NSF_UP_AND_DOWN_3X6') },
            ],
        ]),
    );

    const { Event } = useEventCommonStoreRef();

    const Locales = useLocaleStore().locales;

    const FederationsLoader = ref(
        new LoaderWithReset<Federation[]>({ defaultValue: [], loaderFunction: () => BaseObjDao.Load(Federation) }),
    );
    const PointTypesLoader = ref(new LoaderWithReset<PointType[]>({ defaultValue: [], loaderFunction: () => BaseObjDao.Load(PointType) }));
    const OrganizersLoader = ref(
        new LoaderWithReset<Organizer[]>({ defaultValue: [], loaderFunction: () => OrganizerDao.GetOrganizersForUser() }),
    ); // TODO: Should be for user how??
    const IDTypesLoader = ref(
        new LoaderWithReset<ParticipantIDType[]>({ defaultValue: [], loaderFunction: () => BaseObjDao.Load(ParticipantIDType) }),
    );
    const CountriesLoader = ref(new LoaderWithReset<Country[]>({ defaultValue: [], loaderFunction: () => BaseObjDao.Load(Country) }));
    const StationTypeLoader = ref(
        new LoaderWithReset<StationType[]>({ defaultValue: [], loaderFunction: () => BaseObjDao.Load(StationType) }),
    );
    const ClubsLoader = ref(new LoaderWithReset<Club[]>({ defaultValue: [], loaderFunction: () => BaseObjDao.Load(Club) }));
    const HeatSortPlansLoader = ref(
        new LoaderWithReset<HeatSortPlan[]>({ defaultValue: [], loaderFunction: () => BaseObjDao.Load(HeatSortPlan) }),
    );

    const RegionsLoader = ref(new LoaderWithReset<Region[]>({ defaultValue: [], loaderFunction: () => BaseObjDao.Load(Region) }));

    // make sure we have loaded ds
    const ensureLoaded = async (dataTypeToEnsureIsLoaded: CommonValues[]) => {
        // typed

        const promises = [];

        for (const ds of dataTypeToEnsureIsLoaded) {
            if (ds == 'HeatSortPlans') {
                promises.push(HeatSortPlansLoader.value.Load());
            }
            if (ds == 'PointTypes') {
                promises.push(PointTypesLoader.value.Load());
            }
            if (ds == 'Organizers') {
                promises.push(OrganizersLoader.value.Load());
            }
            if (ds == 'Federations') {
                promises.push(FederationsLoader.value.Load());
            }
            if (ds == 'IDTypes') {
                promises.push(IDTypesLoader.value.Load());
            }
            if (ds == 'Countries') {
                promises.push(CountriesLoader.value.Load());
            }
            if (ds == 'Clubs') {
                promises.push(ClubsLoader.value.Load());
            }
            if (ds == 'StationType') {
                promises.push(StationTypeLoader.value.Load());
            }
            if (ds == 'Regions') {
                promises.push(RegionsLoader.value.Load());
            }
        }

        await Promise.all(promises);
    };

    // SelectLine
    const PointTypesAsSelectLine = computed<Map<SelectLineValueType, SelectLine>>(() => {
        const map = new Map<SelectLineValueType, SelectLine>();
        PointTypesLoader.value.Value.forEach((x) => {
            map.set(x.ID, { text: x.Name, value: x.ID });
        });
        return map;
    });

    const Organizers = computed(() => OrganizersLoader.value.Value);

    const OrganizersAsSelectLine = computed<Map<SelectLineValueType, SelectLine>>(() => {
        const map = new Map<SelectLineValueType, SelectLine>();
        Organizers.value.forEach((x) => {
            map.set(x.ID, { text: x.Name, value: x.ID });
        });
        return map;
    });

    const Sports = computed(() => FederationsLoader.value.Value.flatMap((x) => x.Sports));
    const SportLevels = computed(() => FederationsLoader.value.Value.flatMap((x) => x.Sports.flatMap((y) => y.SportLevels)));
    const Disciplines = computed(() => FederationsLoader.value.Value.flatMap((x) => x.Sports.flatMap((y) => y.Disciplines)));

    const SportsForEvent = computed(() =>
        FederationsLoader.value.Value.filter((x) => x.ID == Event.value?.FederationID).flatMap((x) => x.Sports),
    );
    const SportLevelsForEvent = computed(() =>
        FederationsLoader.value.Value.flatMap((x) => x.Sports.filter((x) => x.ID == Event.value?.SportID).flatMap((y) => y.SportLevels)),
    );
    const DisciplinesForEvent = computed(() =>
        FederationsLoader.value.Value.flatMap((x) => x.Sports.filter((x) => x.ID == Event.value?.SportID).flatMap((y) => y.Disciplines)),
    );

    const FederationsForOrganizerAsSelectLine = computed<Map<SelectLineValueType, SelectLine>>(() => {
        const map = new Map<SelectLineValueType, SelectLine>();
        FederationsLoader.value.Value.filter((x) => x.CountryID == Event.value?.Organizer?.CountryID).forEach((x) => {
            map.set(x.ID, { text: x.Name, value: x.ID });
        });
        return map;
    });

    /**
     * Return all the station types as SelectLine objects.
     * @remarks this will use translationkey, since we dont support the translation in the database. We generate the translationkey from the name by prefix with "stationtype.".
     */
    const StationTypeAsSelectLine = computed<Map<SelectLineValueType, SelectLine>>(() => {
        const map = new Map<SelectLineValueType, SelectLine>();
        StationTypeLoader.value.Value.forEach((x) => {
            map.set(x.ID, { text: createTranslationKey('stationtype', x.Name), value: x.ID });
        });
        return map;
    });

    /**
     * Return all the station types as SelectLine objects, with an additional option to use the event's station type.
     */
    const StationTypeWithUseStationAsSelectLine = computed<Map<SelectLineValueType, SelectLine>>(() => {
        const map = new Map<SelectLineValueType, SelectLine>();
        map.set(null, { text: 'stationtype.usestation', value: null });
        StationTypeLoader.value.Value.forEach((x) => {
            map.set(x.ID, { text: createTranslationKey('stationtype', x.Name), value: x.ID });
        });
        return map;
    });

    return {
        // typed
        PointTypes: computed(() => PointTypesLoader.value.Value),
        Organizers: Organizers,
        // federation and children
        Federations: computed(() => FederationsLoader.value.Value),
        IDTypes: computed(() => IDTypesLoader.value.Value),
        Countries: computed(() => CountriesLoader.value.Value),
        Clubs: computed(() => ClubsLoader.value.Value),
        ClubsMap: computed(() => {
            const map = new Map<number, Club>();
            ClubsLoader.value.Value.forEach((club) => {
                map.set(club.ID, club);
            });
            return map;
        }),
        Sports: Sports,
        SportLevels: SportLevels,
        SportsForEvent: SportsForEvent, // TODO: Should be moved to useEventCommonStoreRef
        SportForEvent: computed(() => SportsForEvent.value.find((x) => x.ID == Event.value?.SportID)), // TODO: Should be moved to useEventCommonStoreRef
        SportLevelsForEvent: SportLevelsForEvent,
        Disciplines: Disciplines,
        DisciplinesForEvent: DisciplinesForEvent,

        StationTypes: computed(() => StationTypeLoader.value.Value),

        Regions: computed(() => RegionsLoader.value.Value),
        RegionsMap: computed(() => {
            const map = new Map<number, Region>();
            RegionsLoader.value.Value.forEach((region) => {
                map.set(region.ID, region);
            });
            return map;
        }),

        // static
        Genders: Genders,
        PrizeShareTypes: PrizeShareTypes,
        DecimalPlacesInResults: DecimalPlacesInResults,
        RankingResolutions: RankingResolutions,
        RoundTypes: RoundTypes,
        SortDirection: SortDirections,
        TimeSelect: TimeSelect,
        ContestantStatus: ContestantStatus,

        // SelectLine
        PointTypesAsSelectLine: PointTypesAsSelectLine,
        StationTypeAsSelectLine: StationTypeAsSelectLine,
        StationTypeWithUseStationAsSelectLine: StationTypeWithUseStationAsSelectLine,

        OrganizersAsSelectLine: OrganizersAsSelectLine,
        GendersAsSelectLine: Genders,
        PrizeShareTypesAsSelectLine: PrizeShareTypes,
        DecimalPlacesInResultsAsSelectLine: DecimalPlacesInResults,
        RankingResolutionsAsSelectLine: RankingResolutions,
        RoundTypesAsSelectLine: RoundTypes,
        ContestantStatusAsSelectLine: ContestantStatus,
        SortDirectionAsSelectLine: SortDirections,
        TimeSelectAsSelectLine: TimeSelect,
        CommunicationTypeAsSelectLine: CommunicationTypeAsSelectLine,
        CupTypeAsSelectLine: CupTypeAsSelectLine,
        OrganizersWithAllAsSelectLine: computed<Map<SelectLineValueType, SelectLine>>(() => {
            const map = new Map<SelectLineValueType, SelectLine>();
            map.set(null, { text: 'general.all', value: null });
            Organizers.value.forEach((x) => {
                map.set(x.ID, { text: x.Name, value: x.ID });
            });
            return map;
        }),
        IDTypesAsSelectLine: computed<Map<SelectLineValueType, SelectLine>>(() => {
            const map = new Map<SelectLineValueType, SelectLine>();
            IDTypesLoader.value.Value.forEach((x) => {
                map.set(x.ID, { text: x.Name, value: x.ID });
            });
            return map;
        }),
        // federation and children
        FederationsAsSelectLine: computed<Map<SelectLineValueType, SelectLine>>(() => {
            const map = new Map<SelectLineValueType, SelectLine>();
            FederationsLoader.value.Value.forEach((x) => {
                map.set(x.ID, { text: x.Name, value: x.ID });
            });
            return map;
        }),
        FederationsForOrganizerAsSelectLine: FederationsForOrganizerAsSelectLine,
        SportsAsSelectLine: computed<Map<SelectLineValueType, SelectLine>>(() => {
            const map = new Map<SelectLineValueType, SelectLine>();
            Sports.value.forEach((x) => {
                map.set(x.ID, { text: x.Name, value: x.ID });
            });
            return map;
        }),
        SportLevelsAsSelectLine: computed<Map<SelectLineValueType, SelectLine>>(() => {
            const map = new Map<SelectLineValueType, SelectLine>();
            SportLevels.value.forEach((x) => {
                map.set(x.ID, { text: x.Name, value: x.ID });
            });
            return map;
        }),
        SportsForEventAsSelectLine: computed<Map<SelectLineValueType, SelectLine>>(() => {
            const map = new Map<SelectLineValueType, SelectLine>();
            SportsForEvent.value.forEach((x) => {
                map.set(x.ID, { text: x.Name, value: x.ID });
            });
            return map;
        }),
        SportLevelsForEventAsSelectLine: computed<Map<SelectLineValueType, SelectLine>>(() => {
            const map = new Map<SelectLineValueType, SelectLine>();
            SportLevelsForEvent.value.forEach((x) => {
                map.set(x.ID, { text: x.Name, value: x.ID });
            });
            return map;
        }),
        DisciplinesAsSelectLine: computed<Map<SelectLineValueType, SelectLine>>(() => {
            const map = new Map<SelectLineValueType, SelectLine>();
            Disciplines.value.forEach((x) => {
                map.set(x.ID, { text: x.Name, value: x.ID });
            });
            return map;
        }),
        SportsForEventWithUseEventAsSelectLine: computed<Map<SelectLineValueType, SelectLine>>(() => {
            const map = new Map<SelectLineValueType, SelectLine>();
            map.set(null, { text: 'general.useevent', value: null });
            SportsForEvent.value.forEach((x) => {
                map.set(x.ID, { text: x.Name, value: x.ID });
            });
            return map;
        }),

        SportLevelsForEventWithUseEventAsSelectLine: computed<Map<SelectLineValueType, SelectLine>>(() => {
            const map = new Map<SelectLineValueType, SelectLine>();
            map.set(null, { text: 'general.useevent', value: null });
            SportLevelsForEvent.value.forEach((x) => {
                map.set(x.ID, { text: `${x.Name}${x.Inactive ? ' ' + $tm('general.inactive') : ''}`, value: x.ID });
            });
            return map;
        }),
        DisciplinesForEventWithUseEventAsSelectLine: computed<Map<SelectLineValueType, SelectLine>>(() => {
            const map = new Map<SelectLineValueType, SelectLine>();
            map.set(null, { text: 'general.useevent', value: null });
            DisciplinesForEvent.value.forEach((x) => {
                map.set(x.ID, { text: x.Name, value: x.ID });
            });
            return map;
        }),

        LocalesAsSelectLine: computed<Map<SelectLineValueType, SelectLine>>(() => {
            const map = new Map<SelectLineValueType, SelectLine>();
            Locales.forEach((x) => {
                map.set(x.Base, { text: x.Name, value: x.Base, icon: `fi-${x.Iso2}` });
            });
            return map;
        }),

        ClubsAsSelectLine: computed<Map<SelectLineValueType, SelectLine>>(() => {
            const currentEvent = Event.value;
            if (currentEvent == null) {
                return new Map();
            }
            const map = new Map<SelectLineValueType, SelectLine>();
            ClubsLoader.value.Value.filter((x) => !x.isTeam && x.FederationIDS.includes(currentEvent.FederationID)).forEach((x) => {
                map.set(x.ID, { text: x.Name, value: x.ID });
            });
            // Add a new club first in the list
            map.set(null, { text: 'general.none', value: null, separator: true });
            return map;
        }),

        TeamsAsSelectLine: computed<Map<SelectLineValueType, SelectLine>>(() => {
            const currentEvent = Event.value;
            if (currentEvent == null) {
                return new Map();
            }
            const map = new Map<SelectLineValueType, SelectLine>();
            ClubsLoader.value.Value.filter((x) => x.isTeam && x.FederationIDS.includes(currentEvent.FederationID)).forEach((x) => {
                map.set(x.ID, { text: x.Name, value: x.ID });
            });
            // Add a new club first in the list
            map.set(null, { text: 'general.none', value: null, separator: true });
            return map;
        }),

        // SelectLine with null as UseEvent
        DecimalPlacesInResultsWithUseEventAsSelectLine: ref<Map<SelectLineValueType, SelectLine>>(
            new Map<SelectLineValueType, SelectLine>([
                [null, { text: 'general.useevent', value: null }],
                ...Array.from(DecimalPlacesInResults.value.entries()).map((x) => [x[0], x[1]] as [SelectLineValueType, SelectLine]),
            ]),
        ),
        RankingResolutionsWithUseEventAsSelectLine: ref<Map<SelectLineValueType, SelectLine>>(
            new Map<SelectLineValueType, SelectLine>([
                [null, { text: 'general.useevent', value: null }],
                ...Array.from(RankingResolutions.value.entries()).map((x) => [x[0], x[1]] as [SelectLineValueType, SelectLine]),
            ]),
        ),
        RoundTypesWithUseEventAsSelectLine: ref<Map<SelectLineValueType, SelectLine>>(
            new Map<SelectLineValueType, SelectLine>([
                [null, { text: 'general.useevent', value: null }],
                ...Array.from(RoundTypes.value.entries()).map((x) => [x[0], x[1]] as [SelectLineValueType, SelectLine]),
            ]),
        ),

        RegionsAsSelectLine: computed<Map<SelectLineValueType, SelectLine>>(() => {
            const map = new Map<SelectLineValueType, SelectLine>();
            RegionsLoader.value.Value.forEach((x) => {
                map.set(x.ID, { text: x.Name, value: x.ID });
            });
            return map;
        }),

        /**
         * OBS! You probably want to use this instead:
         * ```
         * useLoaders(AddLoader, ['STATIONROLES', 'STATIONS', 'RACES', 'GROUPS', 'CATEGORIES']);
         * ```
         */
        EnsureLoaded: ensureLoaded,
    };
});

/**
 * A reactive reference to the common store.
 * @returns
 */
export const useCommonStoreRef = () => storeToRefs(useCommonStore());
